import React from 'react'
import PropTypes from 'prop-types'
import {
    trackParkingSearchChangePlaceEvent,
    trackParkingSearchChangeDateEvent,
} from 'commons/js/services/analytics'

import {
    getPlace,
    getStart,
    getEnd,
    getLatitude,
    getLongitude,
} from './searchLenses'
import { Flex, Box, ScreenSwitch, themeColor, Hide, PromoBanner, RemoteConfigConsumer } from '@elparking/components'

import Map, { defaultContainerElement, defaultMapElement } from 'parking-search-map/js/components/Map'
import SearchForm from 'parking-search-map/js/components/SearchForm'
import FilterContainer from 'parking-search-map/js/components/FilterContainer'
import List from 'parking-search-map/js/components/List'
import ParkingCard from 'parking-search-map/js/components/ParkingCard'
import EventsBanner from 'parking-search-map/js/components/EventsBanner'
import MapLoader from 'parking-search-map/js/ui/MapLoader'
import Sidebar from 'parking-search-map/js/ui/Sidebar'
import Content from 'parking-search-map/js/ui/Content'
import SearchFormContainer from 'parking-search-map/js/ui/SearchFormContainer'
import ListContainer from 'parking-search-map/js/ui/ListContainer'
import MapContainer from 'parking-search-map/js/ui/MapContainer'
import Banner from 'parking-search-map/js/ui/Banner'
import CancelationInfo from 'parking-search-map/js/ui/CancelationInfo'
import SortBar from 'parking-search-map/js/components/SortBar'
import { getActiveFilters, getAvailableParkings } from 'parking-search-map/js/util'
import DisabledLayer from '../../ui/DisabledLayer'
import styled from 'styled-components'

import buildPoiEventsParams from './buildPoiEventsParams'
import Routes from 'commons/js/routes/routes'
import ErrorBoundary from 'commons/js/components/ErrorBoundary'
import currentCountry from 'commons/js/country'

const containerElement = defaultContainerElement()
const mapElement = defaultMapElement()
const loadingElement = <MapLoader />

const ListWrapper = styled(Box)`
    background: ${themeColor('white')};
    min-height: 100%;
`

function Parkings ({
    search,
    parkings,
    selectedParkingId,
    markedParkingId,
    isLoading,
    autoCenter,
    error,
    searchPoint,
    setFormSearch,
    setMapSearch,
    selectParking,
    markParking,
    computeDistanceTo,
    buildParkingUrl,
    disableAutoCenter,
    setFilters,
    onToggleFilters,
    onHideFilters,
    filters,
    showFilters,
    onOrderBy,
    order,
    showEventsBanner,
    showFilterBox,
    showMowizTruckBox,
}) {
    return (
        <Flex w='100%' flexDirection='column'>
            <ErrorBoundary>
                <RemoteConfigConsumer value='promo_banner_parking_search'>
                    {({ active = false,
                        text,
                        link_text: linkText,
                        link_url: linkUrl,
                        enable_on_mobile: showOnMobile,
                        campaign,
                    }) => (
                        active && <Box w='100%'>
                            <PromoBanner
                              text={text}
                              linkText={linkText}
                              linkUrl={linkUrl}
                              showOnMobile={showOnMobile}
                              campaign={campaign}
                              id={`promo-bar-${currentCountry}`}
                            />
                        </Box>
                    )}
                </RemoteConfigConsumer>
            </ErrorBoundary>
            <Flex flex='1' style={{ overflow: 'hidden' }}>
                <Sidebar>
                    <SearchFormContainer>
                        <SearchForm
                          showGoogleBanner
                          place={getPlace(search)}
                          startDate={getStart(search)}
                          endDate={getEnd(search)}
                          onSearch={setFormSearch}
                          onChangePlace={trackParkingSearchChangePlaceEvent}
                          onChangeDates={trackParkingSearchChangeDateEvent}
                          setFilters={setFilters}
                          onShowFilters={onToggleFilters}
                          filters={filters}
                          showFilterBox={showFilterBox}
                          showMowizTruckBox={showMowizTruckBox}
                        />
                    </SearchFormContainer>
                    <ListContainer>
                        {showFilterBox &&
                            <FilterContainer
                              isOpen={showFilters}
                              filters={filters}
                              setFilters={setFilters}
                              closeModal={onHideFilters}
                            />

                        }
                        {!showFilters && <ListWrapper>
                            <Banner id='tagManagerBanner' />
                            <Hide xs sm md>
                                <CancelationInfo />
                            </Hide>
                            <SortBar
                              showFilterBox={showFilterBox}
                              parkingsNumber={getAvailableParkings(parkings).length}
                              filterNumber={getActiveFilters(filters)}
                              onShowFilters={onToggleFilters}
                              onOrderBy={onOrderBy}
                              order={order}
                            />
                            {showEventsBanner &&
                                <Box mx={'16px'} mt={'16px'}>
                                    <EventsBanner link={
                                        Routes.poiEventsPath(buildPoiEventsParams({
                                            startDate: getStart(search),
                                            place: getPlace(search),
                                        }))
                                    } />
                                </Box>
                            }
                            <List
                              parkings={parkings}
                              selected={selectedParkingId}
                              marked={markedParkingId}
                              error={error}
                              isLoading={isLoading}
                              cardComponent={ParkingCard}
                              markParking={markParking}
                              computeDistanceTo={computeDistanceTo}
                              buildParkingUrl={buildParkingUrl}
                            />
                            {showFilters && <DisabledLayer onClick={onHideFilters} />}
                        </ListWrapper>}
                    </ListContainer>
                </Sidebar>
                <Content>
                    <MapContainer>
                        <ScreenSwitch
                          medium={Map}
                          searchPoint={searchPoint}
                          autoCenter={autoCenter}
                          selected={selectedParkingId}
                          marked={markedParkingId}
                          latitude={getLatitude(search)}
                          longitude={getLongitude(search)}
                          parkings={parkings}
                          isLoading={isLoading}
                          containerElement={containerElement}
                          mapElement={mapElement}
                          loadingElement={loadingElement}
                          onSearch={setMapSearch}
                          onSelectParking={selectParking}
                          onMarkParking={markParking}
                          disableAutoCenter={disableAutoCenter}
                          computeDistanceTo={computeDistanceTo}
                          buildParkingUrl={buildParkingUrl}
                        />
                        {showFilters && <DisabledLayer onClick={onHideFilters} />}
                    </MapContainer>
                </Content>
            </Flex>
        </Flex>
    )
}

Parkings.propTypes = {
    showFilters: PropTypes.bool,
    search: PropTypes.object,
    parkings: PropTypes.array,
    selectedParkingId: PropTypes.string,
    markedParkingId: PropTypes.number,
    isLoading: PropTypes.bool,
    autoCenter: PropTypes.bool,
    error: PropTypes.object,
    searchPoint: PropTypes.object,
    setFormSearch: PropTypes.func,
    setMapSearch: PropTypes.func,
    selectParking: PropTypes.func,
    markParking: PropTypes.func,
    computeDistanceTo: PropTypes.func,
    buildParkingUrl: PropTypes.func,
    disableAutoCenter: PropTypes.func,
    setFilters: PropTypes.func,
    onHideFilters: PropTypes.func,
    onToggleFilters: PropTypes.func,
    filters: PropTypes.object,
    onOrderBy: PropTypes.func,
    order: PropTypes.string,
    showEventsBanner: PropTypes.bool,
    showFilterBox: PropTypes.bool.isRequired,
    showMowizTruckBox: PropTypes.bool.isRequired,
}

Parkings.defaultProps = {
    onToggleFilters: () => { },
    onHideFilters: () => { },
    setFilters: () => { },
    onOrderBy: () => { },
    filters: {},
    showFilters: false,
    showMowizTruckBox: false,
    showEventsBanner: false,
}

export default Parkings
